import React, { useEffect, useState } from "react";

import { es } from "date-fns/locale";
import dayjs from "dayjs";
import { CalendarIcon, RefreshCw } from "lucide-react";

// Date preset configurations
const DATE_PRESETS = [
  {
    label: "Hoy",
    getRange: () => ({
      start: dayjs().startOf("day"),
      end: dayjs().endOf("day"),
    }),
  },
  {
    label: "Ayer",
    getRange: () => ({
      start: dayjs().subtract(1, "day").startOf("day"),
      end: dayjs().subtract(1, "day").endOf("day"),
    }),
  },
  {
    label: "Esta semana",
    getRange: () => ({
      start: dayjs().startOf("week"),
      end: dayjs(),
    }),
  },
  {
    label: "Semana anterior",
    getRange: () => ({
      start: dayjs().subtract(1, "week").startOf("week"),
      end: dayjs().subtract(1, "week").endOf("week"),
    }),
  },
  {
    label: "Este mes",
    getRange: () => ({
      start: dayjs().startOf("month"),
      end: dayjs(),
    }),
  },
  {
    label: "Mes anterior",
    getRange: () => ({
      start: dayjs().subtract(1, "month").startOf("month"),
      end: dayjs().subtract(1, "month").endOf("month"),
    }),
  },
  {
    label: "Este año",
    getRange: () => ({
      start: dayjs().startOf("year"),
      end: dayjs(),
    }),
  },
  {
    label: "Año anterior",
    getRange: () => ({
      start: dayjs().subtract(1, "year").startOf("year"),
      end: dayjs().subtract(1, "year").endOf("year"),
    }),
  },
];

import { Button } from "~/components/ui/button";
import { Calendar } from "~/components/ui/calendar";
import { Card, CardContent } from "~/components/ui/card";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "~/components/ui/popover";
import { cn } from "~/lib/utils";

interface FacturasFilterBarProps {
  startDate: Date | undefined;
  setStartDate: (date: Date | undefined) => void;
  endDate: Date | undefined;
  setEndDate: (date: Date | undefined) => void;
  onRefresh: () => void;
  className?: string;
}

const FacturasFilterBar: React.FC<FacturasFilterBarProps> = ({
  startDate,
  setStartDate,
  endDate,
  setEndDate,
  onRefresh,
  className,
}) => {
  // Track which preset is currently selected for visual feedback
  const [activePreset, setActivePreset] = useState<string | null>("Este mes");

  // Initialize with start of current month and today
  useEffect(() => {
    if (!startDate || !endDate) {
      const now = dayjs();
      const monthStart = now.startOf("month").toDate();
      const today = now.toDate();

      setStartDate(monthStart);
      setEndDate(today);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run once on mount

  // Handle preset button click
  const handlePresetClick = (preset: (typeof DATE_PRESETS)[0]) => {
    const { start, end } = preset.getRange();
    setStartDate(start.toDate());
    setEndDate(end.toDate());
    setActivePreset(preset.label);
  };

  const handleStartDateChange = (date: Date | undefined) => {
    setStartDate(date);
    setActivePreset(null); // Clear preset on manual change
  };

  const handleEndDateChange = (date: Date | undefined) => {
    setEndDate(date);
    setActivePreset(null); // Clear preset on manual change
  };

  // Reset to default (start of current month and today)
  const handleClearFilters = () => {
    const now = dayjs();
    const monthStart = now.startOf("month").toDate();
    const today = now.toDate();

    setStartDate(monthStart);
    setEndDate(today);
    setActivePreset("Este mes");
  };

  return (
    <Card className={className}>
      <CardContent className="p-4">
        <div className="flex flex-col justify-between gap-4">
          <div className="flex justify-between">
            <div className="text-lg font-medium">Filtros</div>

            {/* Reset Filter Button */}
            <Button
              variant="secondary"
              size="sm"
              onClick={handleClearFilters}
              className="self-start text-muted-foreground hover:text-foreground"
            >
              Restablecer fechas
            </Button>
          </div>

          {/* Quick Date Presets */}
          <div className="flex flex-wrap gap-2">
            {DATE_PRESETS.map((preset) => (
              <Button
                key={preset.label}
                variant={activePreset === preset.label ? "default" : "outline"}
                size="sm"
                onClick={() => handlePresetClick(preset)}
                className="text-xs"
              >
                {preset.label}
              </Button>
            ))}
          </div>

          {/* Date Range Filters */}
          <div className="grid grid-cols-2 gap-3">
            {/* From Date */}
            <div className="space-y-2">
              <span className="text-sm font-medium">Desde:</span>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant={"outline"}
                    className={cn(
                      "w-full justify-start text-left font-normal",
                      !startDate && "text-muted-foreground"
                    )}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {startDate ? (
                      dayjs(startDate).format("DD/MM/YYYY")
                    ) : (
                      <span>Seleccionar fecha</span>
                    )}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={startDate}
                    onSelect={handleStartDateChange}
                    weekStartsOn={1}
                    locale={es}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>

            {/* To Date */}
            <div className="space-y-2">
              <span className="text-sm font-medium">Hasta:</span>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant={"outline"}
                    className={cn(
                      "w-full justify-start text-left font-normal",
                      !endDate && "text-muted-foreground"
                    )}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {endDate ? (
                      dayjs(endDate).format("DD/MM/YYYY")
                    ) : (
                      <span>Seleccionar fecha</span>
                    )}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={endDate}
                    onSelect={handleEndDateChange}
                    weekStartsOn={1}
                    locale={es}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>
          </div>

          {/* Refresh Button */}
          <Button
            variant="outline"
            size="sm"
            onClick={onRefresh}
            className="w-full"
            title="Actualizar datos con período seleccionado"
          >
            <RefreshCw className="mr-2 h-4 w-4" />
            Actualizar
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default FacturasFilterBar;
